// The "Require My Approval" filter for the PRF grid.
//
// One generic shape used to serve every role - my tier is null, the previous tier is not null,
// approvalLevel > N - which stopped matching the rules once the Entry tier was retired. Each
// role now gets a filter built from the tier it actually signs.

// Construction PRFs in these branches carry a Sales Manager tier ahead of the Branch Manager.
// Maintenance PRFs never do, and neither does Construction anywhere else.
const SALES_MANAGER_BRANCHES = [10, 11, 12];

// prfApprovalLevelNeeded only ever returns 0 through 4.
const MAX_APPROVAL_LEVEL = 4;

/**
 * "The computed approval level is at least @minimum."
 *
 * The grid model carries approvalLevel as a string, so this is an equality set rather than a
 * numeric comparison. A row with no computed level matches none of them, which is correct: no
 * tier above the Branch Manager floor is required when no level was computed.
 */
const approvalLevelAtLeast = (minimum) => ({
    logic: 'or',
    filters: Array.from({length: MAX_APPROVAL_LEVEL - minimum + 1}, (_, offset) => ({
        field: 'approvalLevel',
        operator: 'eq',
        value: String(minimum + offset),
    })),
});

/**
 * "Either the Sales Manager has signed, or this row has no Sales Manager tier to sign it."
 *
 * Requiring salesManagerApproval outright excluded every Maintenance row and every
 * ordinary-branch Construction row from the Branch Manager's list.
 */
const salesManagerSignedOrAbsent = () => ({
    logic: 'or',
    filters: [
        {field: 'salesManagerApproval', operator: 'isnotnull'},
        {field: 'prfType', operator: 'neq', value: 'Construction'},
        {
            logic: 'and',
            filters: SALES_MANAGER_BRANCHES.map((branch) => ({
                field: 'branch',
                operator: 'neq',
                value: branch,
            })),
        },
    ],
});

// What "requires my approval" means for each approver role.
//
//   field                the tier's own approval column
//   previousField        the approval that must already be in place to reach the tier
//   precededBySalesManager  the preceding tier exists only on some rows - see the helper above
//   minApprovalLevel     the lowest computed level that makes the tier required
//
// Sales Manager is the first tier and depends on the branch, not on the level. Branch Manager is
// the unconditional floor of the chain, so it carries no level condition either - that is the
// case the floor exists for. Both 'coo' and 'ceo' map to the cooApproval column because that is
// the single "Regional President / CEO" tier those roles sign; the ceoApproval column is kept
// only to display approvals on legacy PRFs and is never required.
const APPROVAL_TIERS = {
    sales_manager: {
        field: 'salesManagerApproval',
    },
    branch_manager: {
        field: 'branchManagerApproval',
        precededBySalesManager: true,
    },
    regional_manager: {
        field: 'regionalManagerApproval',
        previousField: 'branchManagerApproval',
        minApprovalLevel: 2,
    },
    coo: {
        field: 'cooApproval',
        previousField: 'regionalManagerApproval',
        minApprovalLevel: 3,
    },
    ceo: {
        field: 'cooApproval',
        previousField: 'regionalManagerApproval',
        minApprovalLevel: 3,
    },
};

/**
 * Builds the grid filter that leaves only the PRFs waiting on @role.
 *
 * @param {string} role the highest approval tier the user can act on.
 * @returns {object|null} a Kendo filter descriptor, or null if the role signs nothing.
 */
export const buildApprovalFilter = (role) => {
    const tier = APPROVAL_TIERS[role];
    if (!tier) {
        return null;
    }

    const filters = [
        // A draft is waiting on its creator, not on an approver.
        {field: 'submitted', operator: 'eq', value: true},
        // Not yet signed by this tier.
        {field: tier.field, operator: 'isnull'},
    ];

    if (tier.precededBySalesManager) {
        filters.push(salesManagerSignedOrAbsent());
    }

    if (tier.previousField) {
        filters.push({field: tier.previousField, operator: 'isnotnull'});
    }

    if (tier.minApprovalLevel) {
        filters.push(approvalLevelAtLeast(tier.minApprovalLevel));
    }

    return {logic: 'and', filters};
};
